import { TDR } from 'tdr-common';
import config from '../common/config';
import Availability = TDR.Availability;
import { useIsMutating, useQuery, QueryClient } from '@tanstack/react-query';
import { MUTATION_KEY } from './releaseCheckoutSession';
import { useMinimumLoadingTime } from '../hooks/useMinimumLoadingTime';

const TIMESLOTS_CACHE_SETTINGS = {
	staleTime: 2 * 60 * 1000, // 2 minutes
	gcTime: 5 * 60 * 1000 // 5 minutes
};

export function useServiceTimeslots(input: TDR.Availability.getServicesInput) {
	const isReleasing = useIsMutating({
		mutationKey: [MUTATION_KEY],
		exact: true
	});

	const query = useQuery({
		enabled: !isReleasing && !!input.dateStr && !!input.restaurantId,
		queryKey: ['serviceTimeslots', input],
		queryFn: () => getServiceTimeslots(input),
		...TIMESLOTS_CACHE_SETTINGS
	});

	return useMinimumLoadingTime(query);
}

export function prefetchServiceTimeslots(
	queryClient: QueryClient,
	input: TDR.Availability.getServicesInput
) {
	return queryClient.prefetchQuery({
		queryKey: ['serviceTimeslots', input],
		queryFn: () => getServiceTimeslots(input),
		...TIMESLOTS_CACHE_SETTINGS
	});
}

export async function getServiceTimeslots(input: TDR.Availability.getServicesInput): Promise<Availability.getServicesOutput> {
	const response = await fetch(`${config.apiHost}/getServicesTimeslots`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json'
		},
		body: JSON.stringify(input)
	});

	const responsePayload = await response.json() as Availability.getServicesOutput;

	if (!response.ok) {
		throw response;
	}
	else if (!responsePayload?.success) {
		throw Error('Could Not Get Available Time slots');
	}

	return responsePayload;
}